/**
 * REUSABLE COMPONENT - Input
 * Generic input component with validation and icons
 * Can be used in any React project
 */

import React, { forwardRef } from 'react';

const Input = forwardRef(
  (
    {
      label,
      type = 'text',
      placeholder,
      value,
      onChange,
      onBlur,
      error,
      disabled = false,
      required = false,
      fullWidth = true,
      leftIcon = null,
      rightIcon = null,
      helperText,
      className = '',
      inputClassName = '',
      ...props
    },
    ref
  ) => {
    const containerClasses = fullWidth ? 'w-full' : '';

    const inputBaseClasses = 'block px-4 py-2 text-gray-900 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed';

    const inputStateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

    const inputWidthClass = fullWidth ? 'w-full' : '';

    const inputIconPadding = leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '';

    const inputClasses = `${inputBaseClasses} ${inputStateClasses} ${inputWidthClass} ${inputIconPadding} ${inputClassName}`;

    return (
      <div className={`${containerClasses} ${className}`}>
        {label && (
          <label className="block mb-2 text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            type={type}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={inputClasses}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}

        {!error && helperText && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
